"use client";

import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { calculateResizedDimensions } from "./helpers";
import { CanvasFromTemplateProps } from "./types";
import scaleAndPositionObjects from "utils/api/print/scaleAndPositionObjects";

const CanvasFromTemplate = ({ json, dimensions }: CanvasFromTemplateProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fabricCanvasRef = useRef<any>(null);

  // 1st effect: init fabric + canvas once
  useEffect(() => {
    let disposed = false;

    (async () => {
      // Dynamically import fabric in the browser
      const { fabric } = await import("fabric");

      if (!canvasRef.current || disposed) return;

      fabricCanvasRef.current = new fabric.Canvas(canvasRef.current);
    })();

    return () => {
      disposed = true;
      fabricCanvasRef.current?.dispose();
      fabricCanvasRef.current = null;
    };
  }, []);

  // 2nd effect: react to json/dimensions
  useEffect(() => {
    if (!json || !dimensions || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const calculatedDimensions = calculateResizedDimensions({
      width: dimensions.width,
      height: dimensions.height,
      max: dimensions.max,
    });

    const { width, height } = calculatedDimensions;

    canvas.setHeight(height);
    canvas.setWidth(width);

    const formattedJsonObjects = json.objects
      ? json.objects
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((object: any) => {
            const updatedObject = { ...object };

            if (updatedObject.type === "field") {
              updatedObject.type = "textbox";
            }

            if (updatedObject.type === "qr") {
              updatedObject.type = "image";
            }

            if (updatedObject.type === "element") return null;

            updatedObject.selectable = false;
            updatedObject.evented = false;

            if (updatedObject.field) delete updatedObject.field;

            return updatedObject;
          })
          .filter(Boolean)
      : [];

    const formattedJson = {
      objects: formattedJsonObjects,
    };

    canvas.loadFromJSON(formattedJson, () => {
      console.log("Canvas loaded from JSON");
      try {
        scaleAndPositionObjects(canvas, formattedJson);
      } catch (error) {
        console.error("Error in scaleAndPositionObjects:", error);
      }
    });
  }, [json, dimensions]);

  const canvasStyles = {
    boxShadow: json?.objects?.length ? "0 0 6px 0 rgba(0, 0, 0, 0.2)" : "none",
  };

  if (!json || !dimensions) return null;

  return (
    <Box sx={canvasStyles}>
      <canvas ref={canvasRef} />
    </Box>
  );
};

export default CanvasFromTemplate;
